import React from 'react';
import { Loader2, UserPlus, Users, Trash2, X, Mail, ChevronRight } from 'lucide-react';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { displayedName } from '../../data/publisherConfigs';
import { ApiError } from '../../lib/apiClient';
import Banner from '../../components/self-service/shared/Banner';
import { rankOf, ROLE_LABEL } from '../../components/self-service/shared/roleRank';
import {
  stationTeamService,
  type InviteResult,
  type InviteResultSummary,
  type TeamPageData,
} from '../../services/stationTeamService';

const PublisherTeamPage: React.FC = () => {
  const { slug, config, user } = useSingleStationContext();
  const callerRank = rankOf(user.rawRole);

  const [data, setData] = React.useState<TeamPageData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = React.useState(false);
  // CTV-764: one team member at a time — name (optional), email, role.
  const [inviteName, setInviteName] = React.useState('');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<'sales_rep' | 'manager'>('sales_rep');
  const [inviting, setInviting] = React.useState(false);
  // Submit failures render INSIDE the modal — the page-level actionError
  // banner sits behind the overlay where the user can't read it.
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [sendingClaimInvites, setSendingClaimInvites] = React.useState(false);
  const [inviteSummary, setInviteSummary] =
    React.useState<InviteResultSummary | null>(null);
  const [legendOpen, setLegendOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await stationTeamService.listTeam(slug);
      setData(resp);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to load team.',
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const resetInviteForm = () => {
    setInviteName('');
    setInviteEmail('');
    setInviteRole('sales_rep');
    setInviteError(null);
  };

  const handleSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setInviteSummary(null);
    setInviteError(null);
    try {
      const summary = await stationTeamService.inviteTeamMembers(slug, [
        {
          email,
          role: inviteRole,
          ...(inviteName.trim() ? { name: inviteName.trim() } : {}),
        },
      ]);
      setInviteSummary(summary);
      // Clear the form when the invite landed; keep it on a rejection or an
      // already-pending duplicate so the user can fix and retry.
      const status = summary.results[0]?.status;
      if (
        status === 'invite_sent' ||
        status === 'access_added' ||
        status === 'already_has_access'
      ) {
        resetInviteForm();
      }
      await refresh();
    } catch (err) {
      setInviteSummary(null);
      const raw =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to send the invite.';
      // Translate DTO-speak ("invites.0.email must be an email") into the
      // user's words; pass anything else through untouched.
      setInviteError(
        /must be an email/i.test(raw) ? 'Enter a valid email address.' : raw,
      );
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    if (!window.confirm(`Revoke pending invite for ${email}?`)) return;
    try {
      await stationTeamService.revokeInvite(slug, inviteId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to revoke invite.',
      );
    }
  };

  /**
   * Turn the publisher's pre-authorized team members into real invites.
   *
   * Claims already grant access on first sign-in, so this is purely a nudge:
   * it runs the same four collision rules as any other invite. Only reachable
   * here, on the Team tab, which by construction means the station has
   * already been finalized.
   */
  const handleSendClaimInvites = async () => {
    // CTV-764: claims carry their own role and name from the onboarding
    // form, so the invites they mint match what the publisher declared.
    const invites = (data?.pendingClaims ?? [])
      .filter((c) => !c.conflictCode)
      .map((c) => ({
        email: c.email,
        role: c.role,
        ...(c.name ? { name: c.name } : {}),
      }));
    if (invites.length === 0) return;
    setSendingClaimInvites(true);
    setInviteSummary(null);
    try {
      const summary = await stationTeamService.inviteTeamMembers(slug, invites);
      setInviteSummary(summary);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to send invites.');
    } finally {
      setSendingClaimInvites(false);
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      await stationTeamService.resendInvite(slug, inviteId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to resend invite.',
      );
    }
  };

  const handleRemoveRep = async (userId: string, email: string) => {
    if (!window.confirm(`Remove ${email} from ${displayedName(config)}?`)) return;
    try {
      await stationTeamService.removeRep(slug, userId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to remove rep.',
      );
    }
  };

  const stationName = displayedName(config);

  // CTV-787: the Team tab of Station settings. The page-level heading and the
  // manager+ gate moved to PublisherSettingsLayout, which owns both tabs, so
  // this renders as a section inside the layout's Outlet.
  return (
    <div>
      {/* Same shape as AdminPageHeader one level down: the action sits beside
          the heading, the lead runs full width underneath. Putting the button
          after the lead in one flex row wraps it onto its own line, because
          the lead is long enough to claim the width. */}
      <div className="mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-semibold text-slate-900">Team</h2>
          <button
            type="button"
            data-testid="invite-team-member"
            onClick={() => {
              setInviteOpen(true);
              setInviteSummary(null);
            }}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            <UserPlus className="w-4 h-4" />
            Invite Team Member
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {`Add and remove team members for ${stationName}. Sales reps handle their own referrals; managers also run submissions, billing, and the team.`}
        </p>
      </div>

      {actionError && (
        <div className="mb-4">
          <Banner
            tone="warn"
            label="Action failed"
            rightSlot={
              <button
                type="button"
                onClick={() => setActionError(null)}
                className="text-rose-600 hover:text-rose-800"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            }
          >
            {actionError}
          </Banner>
        </div>
      )}

      {error ? (
        <Banner tone="warn" label="Couldn't load team">{error}</Banner>
      ) : loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : data ? (
        <>
          <section className="bg-white border border-slate-200 rounded-xl mb-6">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">Active Team Members</h3>
              <span className="ml-auto text-xs text-slate-500 tabular-nums">
                {data.activeReps.length}
              </span>
            </div>
            <div className="px-5 py-2 border-b border-slate-100">
              <button
                type="button"
                onClick={() => setLegendOpen((v) => !v)}
                aria-expanded={legendOpen}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${legendOpen ? 'rotate-90' : ''}`}
                />
                Role Tags
              </button>
              {legendOpen && (
                <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
                  <dt>
                    <RoleBadge role="organization_admin" />
                  </dt>
                  <dd className="text-slate-600 self-center">
                    Access to every station in this organization.
                  </dd>
                  <dt>
                    <RoleBadge role="manager" />
                  </dt>
                  <dd className="text-slate-600 self-center">
                    Runs this station: submissions, billing, and the team,
                    plus their own referrals.
                  </dd>
                  <dt>
                    <RoleBadge role="sales_rep" />
                  </dt>
                  <dd className="text-slate-600 self-center">
                    Brings in advertisers and tracks their own referrals.
                  </dd>
                </dl>
              )}
            </div>
            {data.activeReps.length === 0 ? (
              <div className="px-5 py-6 text-sm text-slate-500">
                No active team members yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.activeReps.map((rep) => {
                  const isSelf = rep.userId === user.id;
                  // Server filters out super_admins from the list, so this
                  // branch is defensive only — if one slips through (e.g.,
                  // metadata race), keep the row but hide Remove.
                  const isSuperAdmin = rep.role === 'super_admin';
                  // Org_admins are read-only via this surface — their access
                  // is role-based and only manageable on the legacy admin
                  // Users page. Hide Remove entirely (matches super_admin).
                  const isOrgAdmin = rep.role === 'organization_admin';
                  const removeButtonHidden = isSuperAdmin || isOrgAdmin;
                  // Rank check mirrors the server: a caller can remove peers
                  // and below, but not roles ranked higher than their own.
                  const targetRank = rankOf(rep.role);
                  const insufficientRank = targetRank > callerRank;
                  const disabledRemove = isSelf || insufficientRank;
                  const removeTitle = isSelf
                    ? "You can't remove yourself; ask a teammate"
                    : insufficientRank
                      ? `Only an ${ROLE_LABEL[rep.role] ?? rep.role} (or higher) can remove this user`
                      : `Remove ${rep.email}`;
                  return (
                    <li
                      key={rep.userId}
                      className="px-5 py-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {rep.name ?? rep.email}
                          {isSelf && (
                            <span className="ml-1.5 text-xs text-slate-500">
                              · You
                            </span>
                          )}
                        </div>
                        {rep.name && (
                          <div className="text-xs text-slate-500 truncate">
                            {rep.email}
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          <RoleBadge role={rep.role} />
                          {rep.otherStationCount > 0 && (
                            <span className="ml-2">
                              +{rep.otherStationCount} other station
                              {rep.otherStationCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                      {!removeButtonHidden && (
                        <button
                          type="button"
                          title={removeTitle}
                          disabled={disabledRemove}
                          onClick={() => handleRemoveRep(rep.userId, rep.email)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Only rendered once the publisher's onboarding form has been
              finalized into this station, since that is what creates claims. */}
          {data.pendingClaims.length > 0 && (
            <section className="bg-white border border-slate-200 rounded-xl">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-900">
                  From the Onboarding Form
                </h3>
                <span className="ml-auto text-xs text-slate-500 tabular-nums">
                  {data.pendingClaims.length}
                </span>
              </div>
              <div className="px-5 py-3 text-[12px] text-slate-500 border-b border-slate-100">
                These people were added when the station was set up. Their access
                applies automatically the first time they sign in with the email
                below, so sending an invite email is optional.
              </div>
              <ul className="divide-y divide-slate-100">
                {data.pendingClaims.map((claim) => (
                  <li
                    key={claim.id}
                    className="px-5 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {claim.name ?? claim.email}
                      </div>
                      {claim.name && (
                        <div className="text-xs text-slate-500 truncate">{claim.email}</div>
                      )}
                      {/* Badge under the name, matching the Active and Pending rows. */}
                      <div className="text-xs text-slate-500 mt-0.5">
                        <RoleBadge role={claim.role} />
                      </div>
                      {claim.conflictCode === 'EMAIL_BELONGS_TO_OTHER_ORG' && (
                        <div className="text-xs text-rose-600 mt-0.5">
                          This email already belongs to another organization. A super admin
                          has to sort this one out by hand.
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => void handleSendClaimInvites()}
                  disabled={
                    sendingClaimInvites ||
                    data.pendingClaims.every((c) => c.conflictCode)
                  }
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {sendingClaimInvites && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {sendingClaimInvites ? 'Sending…' : 'Send Invites'}
                </button>
              </div>
            </section>
          )}

          <section className="bg-white border border-slate-200 rounded-xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">
                Pending Invites
              </h3>
              <span className="ml-auto text-xs text-slate-500 tabular-nums">
                {data.pendingInvites.length}
              </span>
            </div>
            {data.pendingInvites.length === 0 ? (
              <div className="px-5 py-6 text-sm text-slate-500">
                No pending invites.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.pendingInvites.map((invite) => (
                  <li
                    key={invite.id}
                    className="px-5 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {invite.name ?? invite.email}
                      </div>
                      {invite.name && (
                        <div className="text-xs text-slate-500 truncate">
                          {invite.email}
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-0.5">
                        <RoleBadge role={invite.role} />
                        <span className="ml-2">
                          Invited {new Date(invite.invitedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleResendInvite(invite.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleRevokeInvite(invite.id, invite.email)
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}

      {inviteOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => {
              setInviteOpen(false);
              setInviteSummary(null);
              resetInviteForm();
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Invite a Team Member
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Inviting to{' '}
                  <span className="font-medium text-slate-900">
                    {stationName}
                  </span>
                  .
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setInviteOpen(false);
                  setInviteSummary(null);
                  resetInviteForm();
                }}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitInvite}>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="invite-name"
                    className="block text-xs font-medium text-slate-700 mb-1"
                  >
                    Name <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="invite-name"
                    data-testid="invite-name"
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Jordan Smith"
                    maxLength={200}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="invite-email"
                    className="block text-xs font-medium text-slate-700 mb-1"
                  >
                    Email
                  </label>
                  <input
                    id="invite-email"
                    data-testid="invite-email"
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="jordan@example.com"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <span className="block text-xs font-medium text-slate-700 mb-1">
                    Role
                  </span>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Role">
                    <label
                      className={`flex flex-col gap-0.5 border rounded-md px-3 py-2 cursor-pointer ${
                        inviteRole === 'sales_rep'
                          ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/50'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="invite-role"
                        data-testid="invite-role-sales-rep"
                        className="sr-only"
                        checked={inviteRole === 'sales_rep'}
                        onChange={() => setInviteRole('sales_rep')}
                      />
                      <span className="text-sm font-medium text-slate-900">Sales Rep</span>
                      <span className="text-[11px] text-slate-500">
                        Brings in advertisers and tracks their own referrals.
                      </span>
                    </label>
                    <label
                      className={`flex flex-col gap-0.5 border rounded-md px-3 py-2 cursor-pointer ${
                        inviteRole === 'manager'
                          ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/50'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="invite-role"
                        data-testid="invite-role-manager"
                        className="sr-only"
                        checked={inviteRole === 'manager'}
                        onChange={() => setInviteRole('manager')}
                      />
                      <span className="text-sm font-medium text-slate-900">Manager</span>
                      <span className="text-[11px] text-slate-500">
                        Also runs submissions, billing, and the team.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setInviteOpen(false);
                    setInviteSummary(null);
                    resetInviteForm();
                  }}
                  disabled={inviting}
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="invite-submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Send Invite
                </button>
              </div>
            </form>

            {inviteError && (
              <div className="mt-4" data-testid="invite-error">
                <Banner tone="warn" label="Couldn't send the invite">
                  {inviteError}
                </Banner>
              </div>
            )}

            {inviteSummary && (
              <div className="mt-4 space-y-2">
                <InviteResultBreakdown
                  summary={inviteSummary}
                  stationName={stationName}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// CTV-764: granular role badges. This reverses the CTV-583 collapse (which
// showed one "Station Rep" tag for sales_rep AND manager) — with managers now
// mintable from this page, the badge must state the real role.
const ROLE_TONE: Record<string, string> = {
  organization_admin: 'bg-violet-100 text-violet-800',
  manager: 'bg-sky-100 text-sky-800',
  sales_rep: 'bg-slate-100 text-slate-700',
};

const BADGE_LABEL: Record<string, string> = {
  organization_admin: 'Org Admin',
  manager: 'Manager',
  sales_rep: 'Sales Rep',
  super_admin: 'Super Admin',
};

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${ROLE_TONE[role] ?? 'bg-slate-100 text-slate-700'}`}
    >
      {BADGE_LABEL[role] ?? role}
    </span>
  );
}

// Per-recipient breakdown of an invite batch. Each outcome gets its own
// labeled banner so the user can see exactly which email landed in which
// category — counts alone (e.g., "1 already had access") were confusing
// because the email wasn't named.
function InviteResultBreakdown({
  summary,
  stationName,
}: {
  summary: InviteResultSummary;
  stationName: string;
}) {
  const groupBy = (statuses: InviteResult['status'][]) =>
    summary.results.filter((r) => statuses.includes(r.status));

  const sent = groupBy(['invite_sent']);
  const added = groupBy(['access_added']);
  const skipped = groupBy(['already_has_access']);
  // CTV-764: pending duplicate — blocked so role changes are deliberate.
  const alreadyInvited = groupBy(['already_invited']);
  // Split "already had access" by role so the copy can be specific —
  // org_admins/super_admins have global access that isn't actionable here;
  // sales_reps/managers are just already-fully-active on this station.
  const skippedOrgWide = skipped.filter(
    (r) => r.targetRole === 'organization_admin' || r.targetRole === 'super_admin',
  );
  const skippedStationScoped = skipped.filter(
    (r) => !(r.targetRole === 'organization_admin' || r.targetRole === 'super_admin'),
  );
  const rejectedOtherOrg = groupBy(['rejected_other_org']);
  // CTV-673 audit: buyer-side accounts are rejected outright.
  const rejectedAdvertiser = groupBy(['rejected_advertiser_email']);
  const rejectedInvalid = groupBy(['rejected_invalid_email']);

  return (
    <>
      {sent.length > 0 && (
        <Banner
          tone="success"
          label={sent.length === 1 ? 'Invite sent' : `Invites sent (${sent.length})`}
        >
          <p className="mb-1">
            {sent.length === 1 ? 'This person' : 'These people'} will get an
            email invitation to join {stationName}, and will appear under{' '}
            <strong>Pending Invites</strong> until they accept.
          </p>
          <ul className="list-disc list-inside">
            {sent.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {added.length > 0 && (
        <Banner
          tone="success"
          label={
            added.length === 1
              ? `Added to ${stationName}`
              : `Added to ${stationName} (${added.length})`
          }
        >
          <p className="mb-1">
            {added.length === 1
              ? 'This person already had a CTVBuyer account in your organization.'
              : 'These people already had CTVBuyer accounts in your organization.'}{' '}
            We've added <strong>{stationName}</strong> to their station list, so
            no invitation email was needed. They got a heads-up email with a
            link to the admin dashboard.
          </p>
          <ul className="list-disc list-inside">
            {added.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {skippedOrgWide.length > 0 && (
        <Banner
          tone="info"
          label={
            skippedOrgWide.length === 1
              ? 'Already had access (org-wide role)'
              : `Already had access, org-wide role (${skippedOrgWide.length})`
          }
        >
          <p className="mb-1">
            {skippedOrgWide.length === 1
              ? 'This person is an organization admin and already has'
              : 'These people are organization admins and already have'}{' '}
            access to every station in this organization. The Team tab can't
            add or remove their access; that's managed on the admin Users page.
          </p>
          <ul className="list-disc list-inside">
            {skippedOrgWide.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {skippedStationScoped.length > 0 && (
        <Banner
          tone="info"
          label={
            skippedStationScoped.length === 1
              ? 'Already had access'
              : `Already had access (${skippedStationScoped.length})`
          }
        >
          <p className="mb-1">
            {skippedStationScoped.length === 1
              ? 'This person is already an active team member on'
              : 'These people are already active team members on'}{' '}
            <strong>{stationName}</strong>. No change was made.
          </p>
          <ul className="list-disc list-inside">
            {skippedStationScoped.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {alreadyInvited.length > 0 && (
        <Banner
          tone="info"
          label={
            alreadyInvited.length === 1
              ? 'Already invited'
              : `Already invited (${alreadyInvited.length})`
          }
        >
          <p className="mb-1">
            {alreadyInvited.length === 1
              ? 'This person already has a pending invite to'
              : 'These people already have pending invites to'}{' '}
            <strong>{stationName}</strong>. Use <strong>Resend</strong> on the
            pending invite to nudge them, or revoke it and invite again to
            change the role.
          </p>
          <ul className="list-disc list-inside">
            {alreadyInvited.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
                {r.pendingRole ? ` (invited as ${BADGE_LABEL[r.pendingRole] ?? r.pendingRole})` : ''}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {rejectedOtherOrg.length > 0 && (
        <Banner
          tone="warn"
          label={
            rejectedOtherOrg.length === 1
              ? "Couldn't invite: different publisher"
              : `Couldn't invite: different publisher (${rejectedOtherOrg.length})`
          }
        >
          <p className="mb-1">
            An account with this email already exists at another publisher.
            Contact CTVBuyer ops to merge accounts, or use a different email.
          </p>
          <ul className="list-disc list-inside">
            {rejectedOtherOrg.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {rejectedAdvertiser.length > 0 && (
        <Banner
          tone="warn"
          label={
            rejectedAdvertiser.length === 1
              ? "Couldn't invite: advertiser account"
              : `Couldn't invite: advertiser account (${rejectedAdvertiser.length})`
          }
        >
          <p className="mb-1">
            This email belongs to an advertiser account, which can&apos;t be
            given station-staff access. Use a different email for this person.
          </p>
          <ul className="list-disc list-inside">
            {rejectedAdvertiser.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {rejectedInvalid.length > 0 && (
        <Banner
          tone="warn"
          label={
            rejectedInvalid.length === 1
              ? "Couldn't invite: invalid email"
              : `Couldn't invite: invalid email (${rejectedInvalid.length})`
          }
        >
          <p className="mb-1">Check for typos and try again.</p>
          <ul className="list-disc list-inside">
            {rejectedInvalid.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
                {r.error ? `: ${r.error}` : ''}
              </li>
            ))}
          </ul>
        </Banner>
      )}
    </>
  );
}

export default PublisherTeamPage;
